'use client';

import { createClient } from '@/lib/supabase/client';

export type ComparisonRecord = {
  sessionId: string;
  productIds: string[];
  productNames: string[];
};

function isSchemaError(error: any): boolean {
  if (!error) return false;
  if (error.code && typeof error.code === 'string') {
    const errorClass = error.code.substring(0, 2);
    if (errorClass === '42') return true;
    if (errorClass === '23') return false;
    if (errorClass === '08') return true;
  }
  if (error.message) {
    const schemaErrorPatterns = [
      /relation.*does not exist/i,
      /column.*does not exist/i,
      /syntax error/i,
    ];
    return schemaErrorPatterns.some((p) => p.test(error.message));
  }
  return false;
}

function getOrCreateSessionId(): string {
  if (typeof window === 'undefined') return 'server';
  let sessionId = sessionStorage.getItem('pccargo_session_id');
  if (!sessionId) {
    sessionId = `session_${Date.now()}_${Math.random().toString(36).substring(2, 9)}`;
    sessionStorage.setItem('pccargo_session_id', sessionId);
  }
  return sessionId;
}

export const comparisonService = {
  async saveComparison(products: { id: string; name: string }[]): Promise<void> {
    if (products.length < 2) return;
    const supabase = createClient();
    let sessionId = getOrCreateSessionId();
    try {
      const { error } = await supabase.from('comparison_history').insert({
        session_id: sessionId,
        product_ids: products.map((p) => p.id),
        product_names: products.map((p) => p.name),
      });

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error saving comparison:', error.message);
          throw error;
        }
        console.log('Data error saving comparison:', error.message);
      }
    } catch (error: any) {
      console.log('Comparison save error:', error.message);
    }
  },

  async getHistory(): Promise<ComparisonRecord[]> {
    const supabase = createClient();
    let sessionId = getOrCreateSessionId();
    try {
      const { data, error } = await supabase
        .from('comparison_history')
        .select('*')
        .eq('session_id', sessionId)
        .order('compared_at', { ascending: false })
        .limit(20);

      if (error) {
        if (isSchemaError(error)) {
          console.error('Schema error fetching comparison history:', error.message);
          throw error;
        }
        return [];
      }
      return (
        data?.map((row) => ({
          sessionId: row.session_id,
          productIds: row.product_ids || [],
          productNames: row.product_names || [],
        })) || []
      );
    } catch (error: any) {
      console.log('Comparison history fetch error:', error.message);
      return [];
    }
  },
};
